import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/Eyebrow";
import { api } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const fadeUp = {
  initial: { opacity: 0, y: 10 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.45 },
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/blog/posts?limit=50");
        setPosts(res.data || []);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Layout>
      <SEO
        title="Field Notes — Essays on Florida Transfer Admissions"
        description="Essays on transferring, sequencing, and what nobody at your community college is telling you about the next 18 months. Written by Taimur Ahmed."
        canonical="/blog"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Beyond Valencia — Field Notes",
          author: { "@type": "Person", name: "Taimur Ahmed" },
        }}
      />
      <section className="border-b border-stone/30 bg-bone" data-testid="blog-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-16 lg:pb-20">
          <Eyebrow>Field notes</Eyebrow>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[4.5rem] text-ink leading-[1.04] tracking-tightest mt-5">
            From the desk.
          </h1>
          <p className="mt-7 text-ink-soft text-base sm:text-lg max-w-2xl leading-relaxed">
            Essays on transferring, sequencing, and what nobody at your community college is telling
            you about the next 18 months.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        {loading ? (
          <div className="text-ink-soft text-sm">Loading posts…</div>
        ) : posts.length === 0 ? (
          <div className="text-ink-soft text-sm" data-testid="blog-empty">No posts yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-0 border-t border-l border-stone/40" data-testid="blog-grid">
            {posts.map((p, i) => (
              <motion.article
                key={p.slug}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.04 }}
                className="border-r border-b border-stone/40 bg-bone"
              >
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="block group">
                  {p.hero_image_url && (
                    <div className="aspect-[16/9] overflow-hidden border-b border-stone/40">
                      <img
                        src={p.hero_image_url}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="p-7">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-brass">
                      {new Date(p.published_at).toLocaleDateString(undefined, {
                        month: "long", day: "numeric", year: "numeric",
                      })} · {p.author}
                    </div>
                    <h2 className="font-serif font-medium text-2xl text-ink mt-3 leading-tight tracking-tight group-hover:text-ivy transition-colors">
                      {p.title}
                    </h2>
                    <p className="text-sm text-ink-soft mt-3 leading-relaxed">{p.excerpt}</p>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy">
                      Read <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
